import React, { useState } from 'react';
import {
    categories,
    categoriesRed,
} from 'mayhem-taxonomy';
import Form from '../../Form';
import Alert from '../../Alert';
import HelpArticleLink from '../HelpArticleLink';
import Modal from '../../Modal';
import { useAuth } from '../../../helpers/AuthContext';
import TranslatableField from '../../TranslatableField';
import ProductBookingSettings from './ProductBookingSettings';
import useDebouncedAutosave from './useDebouncedAutosave';

const MADE_BY_OPTIONS = [
    {value: 'seller', label: 'Made by me'},
    {value: 'hand-assembled', label: 'Hand assembled'},
    {value: 'licensed', label: 'Licensed'},
    {value: 'vintage', label: 'Vintage'},
    {value: 'collectable', label: 'Collectable'},
    {value: 'resale', label: 'Resale'},
    {value: 'pod', label: 'Print on Demand'},
    {value: 'designer', label: 'Designed by me'},
];

const INSTRUCTIONS_TYPES = ['physical', 'custom', 'commission', 'service', 'rental'];

const bookingFormValues = (booking = {}, required = false) => ({
    enabled: required || booking.enabled === true,
    mode: booking.mode || 'appointment',
    resourceIds: Array.isArray(booking.resourceIds) ? booking.resourceIds : [],
    resourceStrategy: booking.resourceStrategy || 'any',
    confirmation: booking.confirmation || 'instant',
    timezoneDisplay: booking.timezoneDisplay || 'resource',
    durationMinutes: booking.durationMinutes ?? 60,
    granularityMinutes: booking.granularityMinutes ?? 30,
    bufferBeforeMinutes: booking.bufferBeforeMinutes ?? 0,
    bufferAfterMinutes: booking.bufferAfterMinutes ?? 0,
    maxAttendees: booking.maxAttendees ?? 1,
    minNights: booking.minNights ?? 1,
    maxNights: booking.maxNights ?? 30,
    checkinTime: booking.checkinTime || '15:00',
    checkoutTime: booking.checkoutTime || '11:00',
    windowMinutes: booking.windowMinutes ?? 30,
    minNoticeMinutes: booking.minNoticeMinutes ?? 0,
    maxAdvanceDays: booking.maxAdvanceDays ?? 60,
    pricingUnit: booking.pricing?.unit
        || (booking.mode === 'daterange' ? 'night' : 'slot'),
    fees: (booking.pricing?.fees || []).map(fee => ({
        label: fee.label,
        amount: (fee.amount / 100).toFixed(2),
        refundable: fee.refundable === true,
    })),
    cancellationEnabled: Boolean(booking.cancellation),
    freeUntilHours: booking.cancellation?.freeUntilHours ?? 24,
    refundPercent: booking.cancellation?.refundPercent ?? 0,
});

const integerValue = value => parseInt(value, 10);

const buildBookingConfig = booking => {
    if (!booking.enabled) return {enabled: false};

    const config = {
        enabled: true,
        mode: booking.mode,
        resourceIds: booking.resourceIds,
        resourceStrategy: booking.resourceStrategy,
        confirmation: booking.confirmation,
        timezoneDisplay: booking.timezoneDisplay,
        minNoticeMinutes: integerValue(booking.minNoticeMinutes),
        maxAdvanceDays: integerValue(booking.maxAdvanceDays),
        pricing: {
            unit: booking.pricingUnit,
            fees: booking.fees.map(fee => ({
                label: fee.label.trim(),
                amount: Math.round(parseFloat(fee.amount) * 100),
                refundable: fee.refundable,
            })),
        },
        cancellation: booking.cancellationEnabled ? {
            freeUntilHours: integerValue(booking.freeUntilHours),
            refundPercent: integerValue(booking.refundPercent),
        } : null,
    };

    if (booking.mode === 'appointment') {
        Object.assign(config, {
            durationMinutes: integerValue(booking.durationMinutes),
            granularityMinutes: integerValue(booking.granularityMinutes),
            bufferBeforeMinutes: integerValue(booking.bufferBeforeMinutes),
            bufferAfterMinutes: integerValue(booking.bufferAfterMinutes),
            maxAttendees: integerValue(booking.maxAttendees),
        });
    }
    if (booking.mode === 'daterange') {
        Object.assign(config, {
            minNights: integerValue(booking.minNights),
            maxNights: integerValue(booking.maxNights),
            checkinTime: booking.checkinTime,
            checkoutTime: booking.checkoutTime,
        });
    }
    if (booking.mode === 'pickup') {
        config.windowMinutes = integerValue(booking.windowMinutes);
    }
    return config;
};

const cloneBookingValues = booking => ({
    ...booking,
    resourceIds: [...booking.resourceIds],
    fees: booking.fees.map(fee => ({...fee})),
});

const bookingValidationError = booking => {
    if (!booking.enabled) return '';
    if (booking.resourceIds.length === 0) {
        return 'Select at least one calendar for this scheduled product.';
    }
    if (booking.mode === 'daterange'
        && integerValue(booking.minNights) > integerValue(booking.maxNights)) {
        return 'Minimum nights cannot be greater than maximum nights.';
    }
    const invalidFee = booking.fees.some(fee => (
        !fee.label.trim() || fee.amount === '' || Number(fee.amount) < 0
    ));
    if (invalidFee) {
        return 'Each fixed fee needs a label and a non-negative amount.';
    }
    return '';
};

const bookingSummary = booking => {
    const modeSummary = booking.mode === 'daterange'
        ? `${booking.minNights}–${booking.maxNights} nights`
        : booking.mode === 'pickup'
            ? `${booking.windowMinutes}-minute pickup windows`
            : `${booking.durationMinutes}-minute appointments`;
    const resourceSummary = `${booking.resourceIds.length} calendar${
        booking.resourceIds.length === 1 ? '' : 's'
    }`;
    const confirmationSummary = booking.confirmation === 'request'
        ? 'Approval required'
        : 'Instant confirmation';
    return `${modeSummary} · ${resourceSummary} · ${confirmationSummary}`;
};

export default function ProductWizardDetails({
    product,
    onNext,
    onBack,
    onSaveAndExit,
    saving,
    error,
    defaultLanguage,
    onTranslationsChange,
    onAutosave,
}) {
    const { channel } = useAuth();
    const isRed = channel?.isRed;
    const isRental = product?.type === 'rental';

    const existingPayWhatYouWant = product?.settings?.payWhatYouWant || {};
    const supportsPayWhatYouWant = !['subscription', 'upgrade'].includes(product?.type);

    const channelFolders = Array.isArray(channel?.metadata?.folders)
        ? channel.metadata.folders
        : [];
    const foldersEnabled = Boolean(channel?.metadata?.useFolders) && channelFolders.length > 0;
    const folderOptions = [
        {value: '', label: 'No Folder'},
        ...channelFolders.map(folder => ({value: folder.uuid, label: folder.name})),
    ];

    const [values, setValues] = useState({
        folder: product?.folder || '',
        extendedDescription: product?.extendedDescription || '',
        category: product?.category || '',
        tags: (product?.tags || []).join(', '),
        inMarketplace: product?.inMarketplace ?? true,
        isBarterable: product?.isBarterable ?? false,
        madeBy: product?.madeBy || 'seller',
        orderInstructions: product?.orderInstructions || '',
        payWhatYouWant: existingPayWhatYouWant.enabled === true,
        minimumPayment: existingPayWhatYouWant.minCents == null
            ? ''
            : (existingPayWhatYouWant.minCents / 100).toFixed(2),
        maximumPayment: existingPayWhatYouWant.maxCents == null
            ? ''
            : (existingPayWhatYouWant.maxCents / 100).toFixed(2),
        booking: bookingFormValues(product?.settings?.booking, isRental),
    });
    const [validationError, setValidationError] = useState('');
    const [bookingModalOpen, setBookingModalOpen] = useState(false);
    const [bookingDraft, setBookingDraft] = useState(null);
    const [bookingModalError, setBookingModalError] = useState('');

    const buildPayload = () => {
        const tags = values.tags
            .split(',')
            .map(t => t.trim().toLowerCase())
            .filter(Boolean)
            .slice(0, 10);

        const payload = {
            extendedDescription: values.extendedDescription,
            category: values.category,
            tags,
            inMarketplace: values.inMarketplace,
            settings: {
                ...(product?.settings || {}),
                payWhatYouWant: {
                    enabled: supportsPayWhatYouWant && values.payWhatYouWant,
                    minCents: values.minimumPayment === ''
                        ? null
                        : Math.round(parseFloat(values.minimumPayment) * 100),
                    maxCents: values.maximumPayment === ''
                        ? null
                        : Math.round(parseFloat(values.maximumPayment) * 100),
                },
                booking: buildBookingConfig(values.booking),
            },
        };
        if (foldersEnabled) {
            payload.folder = values.folder || null;
        }
        if (!isRed) {
            payload.isBarterable = values.isBarterable;
            payload.madeBy = values.madeBy;
        }
        if (INSTRUCTIONS_TYPES.includes(product?.type)) {
            payload.orderInstructions = values.orderInstructions;
        }
        return payload;
    };

    const validatePayWhatYouWant = (payload) => {
        const config = payload.settings.payWhatYouWant;
        if (!config.enabled) return true;
        const recommendedCents = Number(product?.price);
        if (config.minCents !== null && config.maxCents !== null
            && config.minCents > config.maxCents) {
            setValidationError('Minimum payment cannot be greater than maximum payment.');
            return false;
        }
        if (config.minCents !== null && recommendedCents < config.minCents) {
            setValidationError('Recommended price cannot be less than minimum payment.');
            return false;
        }
        if (config.maxCents !== null && recommendedCents > config.maxCents) {
            setValidationError('Recommended price cannot be greater than maximum payment.');
            return false;
        }
        setValidationError('');
        return true;
    };

    const validateBooking = () => {
        const bookingError = bookingValidationError(values.booking);
        if (bookingError) {
            setValidationError(bookingError);
            return false;
        }
        setValidationError('');
        return true;
    };
    const autosavePayload = buildPayload();
    const autosaveIsValid = !bookingValidationError(values.booking)
        && (() => {
            const config = autosavePayload.settings.payWhatYouWant;
            if (!config.enabled) return true;
            const recommendedCents = Number(product?.price);
            return !(config.minCents !== null && config.maxCents !== null
                && config.minCents > config.maxCents)
                && !(config.minCents !== null && recommendedCents < config.minCents)
                && !(config.maxCents !== null && recommendedCents > config.maxCents);
        })();
    const {cancelAutosave} = useDebouncedAutosave(
        autosaveIsValid ? autosavePayload : null,
        onAutosave,
        {enabled: Boolean(product?.uuid)},
    );

    const openBookingModal = () => {
        setBookingDraft(cloneBookingValues({...values.booking, enabled: true}));
        setBookingModalError('');
        setBookingModalOpen(true);
    };

    const closeBookingModal = () => {
        setBookingModalOpen(false);
        setBookingDraft(null);
        setBookingModalError('');
    };

    const saveBookingDraft = () => {
        const draftError = bookingValidationError(bookingDraft);
        if (draftError) {
            setBookingModalError(draftError);
            return;
        }
        setValues(current => ({
            ...current,
            booking: cloneBookingValues({...bookingDraft, enabled: true}),
        }));
        setValidationError('');
        closeBookingModal();
    };

    const setBookingEnabled = enabled => {
        if (enabled) {
            openBookingModal();
            return;
        }
        setValues(current => ({
            ...current,
            booking: {...current.booking, enabled: false},
        }));
        setValidationError('');
    };

    const categoryOptions = isRed ? categoriesRed : categories;
    const translationsEndpoint = product?.uuid
        ? `/v1/seller/products/${product.uuid}/translations`
        : null;

    const fields = [
        ...(foldersEnabled ? [{
            name: 'folder',
            label: 'Product Folder',
            type: 'select',
            options: folderOptions,
            help: 'Organize this product into one of your storefront folders.',
        }] : []),
        {
            name: 'extendedDescription',
            label: 'Extended Description',
            type: 'textarea',
            rows: 5,
            placeholder: 'Detailed description of your product, shown on the product page.',
            labelAddon: translationsEndpoint ? (
                <TranslatableField
                    endpoint={translationsEndpoint}
                    field="extendedDescription"
                    label="Extended Description"
                    sourceValue={values.extendedDescription}
                    defaultLanguage={defaultLanguage}
                    inputType="textarea"
                    translations={product?.translations}
                    onTranslationsChange={onTranslationsChange}
                />
            ) : null,
        },
        {
            name: 'category',
            label: 'Category',
            type: 'select',
            placeholder: '— Select a category —',
            options: categoryOptions,
        },
        {
            name: 'tags',
            label: 'Tags',
            type: 'textarea',
            rows: 2,
            placeholder: 'handmade, vintage, art',
            help: 'Enter comma-separated tags (up to 10). Example: handmade, vintage, art',
        },
        {
            name: 'inMarketplace',
            label: 'In Marketplace',
            type: 'checkbox',
            placeholder: isRed
                ? 'List this product on the Mayhem Red homepage'
                : 'List this product in the Mayhem Marketplace',
        },
        ...(!isRed ? [
            {
                name: 'isBarterable',
                label: 'Allow Trades / Barter',
                type: 'checkbox',
                placeholder: 'Allow buyers to offer trades for this product',
            },
            {
                name: 'madeBy',
                label: 'Made By',
                type: 'select',
                options: MADE_BY_OPTIONS,
            },
        ] : []),
        ...(INSTRUCTIONS_TYPES.includes(product?.type) ? [{
            name: 'orderInstructions',
            label: 'Order Instructions',
            type: 'textarea',
            rows: 3,
            placeholder: 'Optional instructions to show above the order form.',
            labelAddon: translationsEndpoint ? (
                <TranslatableField
                    endpoint={translationsEndpoint}
                    field="orderInstructions"
                    label="Order Instructions"
                    sourceValue={values.orderInstructions}
                    defaultLanguage={defaultLanguage}
                    inputType="textarea"
                    translations={product?.translations}
                    onTranslationsChange={onTranslationsChange}
                />
            ) : null,
        }] : []),
        ...(supportsPayWhatYouWant ? [{
            name: 'payWhatYouWant',
            label: 'Pay What You Want',
            placeholder: 'Let shoppers choose what they pay',
            type: 'checkbox',
            help: 'The product price set on the Basics step becomes the recommended amount.',
        }] : []),
        ...(supportsPayWhatYouWant && values.payWhatYouWant ? [
            {
                name: 'minimumPayment',
                label: 'Minimum Payment (USD)',
                type: 'number',
                step: 0.01,
                min: 0,
                placeholder: 'Optional',
            },
            {
                name: 'maximumPayment',
                label: 'Maximum Payment (USD)',
                type: 'number',
                step: 0.01,
                min: 0,
                placeholder: 'Optional',
            },
        ] : []),
    ];

    const submitDetails = (onSubmit) => {
        if (!validateBooking()) return;
        const payload = buildPayload();
        if (!validatePayWhatYouWant(payload)) return;
        cancelAutosave();
        onSubmit(payload);
    };

    return (
        <div className="wizard-step">
            <Form fields={fields} values={values} onChange={setValues} />
            {supportsPayWhatYouWant && values.payWhatYouWant && (
                <div className="group">
                    <HelpArticleLink
                        slug="pay-what-you-want"
                        label="Learn about Pay What You Want pricing"
                        variant="link"
                    />
                </div>
            )}
            <div className="group product-booking-setting">
                <label className="checkbox-label" htmlFor="booking-enabled">
                    Scheduling
                </label>
                <label className="checkbox" htmlFor="booking-enabled">
                    <input
                        id="booking-enabled"
                        type="checkbox"
                        checked={values.booking.enabled}
                        disabled={isRental}
                        onChange={event => setBookingEnabled(event.target.checked)}
                    />
                    <span>
                        {isRental
                            ? 'Scheduling is required for rental products'
                            : 'Require shoppers to schedule this product'}
                    </span>
                </label>
                {values.booking.enabled && (
                    <div className="product-booking-summary">
                        <span className={`product-booking-summary__status ${
                            bookingValidationError(values.booking)
                                ? 'product-booking-summary__status--warning'
                                : 'product-booking-summary__status--ready'
                        }`}>
                            {bookingValidationError(values.booking) ? 'Needs setup' : 'Ready'}
                        </span>
                        <span className="product-booking-summary__text">
                            {bookingSummary(values.booking)}
                        </span>
                        <button
                            type="button"
                            className="button secondary small"
                            onClick={openBookingModal}
                        >
                            Modify
                        </button>
                    </div>
                )}
            </div>
            <Modal
                isOpen={bookingModalOpen}
                onClose={closeBookingModal}
                title="Scheduling settings"
                size="large"
                className="product-booking-modal"
                footer={(
                    <div className="product-booking-modal__actions">
                        <button
                            type="button"
                            className="button secondary"
                            onClick={closeBookingModal}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="button primary"
                            onClick={saveBookingDraft}
                        >
                            Save settings
                        </button>
                    </div>
                )}
            >
                {bookingDraft && (
                    <>
                        <div className="product-booking-summary product-booking-draft-summary">
                            <span className={`product-booking-summary__status ${
                                bookingValidationError(bookingDraft)
                                    ? 'product-booking-summary__status--warning'
                                    : 'product-booking-summary__status--ready'
                            }`}>
                                {bookingValidationError(bookingDraft) ? 'Needs setup' : 'Ready'}
                            </span>
                            <span className="product-booking-summary__text">
                                {bookingSummary(bookingDraft)}
                            </span>
                        </div>
                        <ProductBookingSettings
                            price={product?.price}
                            currency={product?.currency}
                            value={bookingDraft}
                            onChange={booking => {
                                setBookingDraft(booking);
                                setBookingModalError('');
                            }}
                        />
                        {bookingModalError && <Alert type="error">{bookingModalError}</Alert>}
                    </>
                )}
            </Modal>
            {(validationError || error) && <Alert type="error">{validationError || error}</Alert>}
            <div className="wizard-nav">
                <button type="button" className="button" onClick={onBack} disabled={saving}>
                    ← Back
                </button>
                <button
                    type="button"
                    className="button secondary"
                    onClick={() => submitDetails(onSaveAndExit)}
                    disabled={saving}
                >
                    Save and Edit Later
                </button>
                <button
                    type="button"
                    className="button primary"
                    onClick={() => submitDetails(onNext)}
                    disabled={saving}
                >
                    {saving ? 'Saving…' : 'Next: Media →'}
                </button>
            </div>
        </div>
    );
}
